import React from "react";
import { PhoneIcon } from "@c4c-web/icons";
import { TextField, Typography } from "@c4c-web/ui";
import { getStaticImageUrl } from "@c4c-web/libs-hooks/libs";
import type { FieldErrors, UseFormRegister } from "react-hook-form";
import type { IAddress, TranslationFn } from "../../../types";
import type {
  IUseAddressFormSetters,
  IUseAddressFormStates,
} from "../../../hooks";
import {
  countryCodeShaper,
  countryFlagIconMap,
  isPhoneNumberValid,
  phoneNumberShaper,
} from "../../../utils";
import type { EStoreIds } from "../../../constants";
import { getPhoneNumberError } from "./address-form.helper";

interface IReceiverDetailFieldsProps {
  address: IAddress;
  addressFormSetters: IUseAddressFormSetters;
  addressFormStates: IUseAddressFormStates;
  errors: FieldErrors<IAddress>;
  formData: IAddress;
  register: UseFormRegister<IAddress>;
  storeId: EStoreIds;
  t: TranslationFn;
}

export const ReceiverDetailFields: React.FC<IReceiverDetailFieldsProps> = ({
  address,
  addressFormSetters,
  addressFormStates,
  errors,
  formData,
  register,
  storeId,
  t,
}) => {
  const countryFlagUrl = getStaticImageUrl(
    countryFlagIconMap[storeId.toUpperCase()],
    true,
    false,
  );

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    addressFormSetters.setReceiverDetails((prevStates) => ({
      ...prevStates,
      phone: e.target.value,
    }));
    const phoneNumber = `${formData.countryCode}${e.target.value}`;
    const isPhoneValid = isPhoneNumberValid(phoneNumber, formData.countryCode);
    addressFormSetters.setErrorStates((prevStates) => ({
      ...prevStates,
      invalidPhoneNumber: !isPhoneValid,
    }));
  };

  return (
    <div className="flex flex-col">
      <Typography weight="bold">
        {t("addressJourney.addressForm.receiverDetails")}
      </Typography>
      <div className="flex flex-col gap-sm">
        <div>
          <TextField
            defaultValue={formData.firstName}
            id="firstName"
            label={t("addressJourney.addressForm.name")}
            {...register("firstName", {
              required: true,
            })}
            error={Boolean(errors.firstName)}
            haveBoldText
            helperText={
              errors.firstName ? t("addressJourney.error.receiverName") : ""
            }
            placeholder={t("addressJourney.addressForm.placeHolders.name")}
          />
        </div>
        <div className="flex gap-xs">
          <div className="w-[130px]">
            <TextField
              defaultValue={
                countryCodeShaper(address.phone ?? "") ||
                address.defaultCountryCode
              }
              disabled
              id="countryCode"
              label={t("addressJourney.addressForm.country")}
              {...register("countryCode", {
                required: true,
              })}
              error={Boolean(errors.countryCode)}
              haveBoldText
              helperText={
                errors.countryCode ? t("addressJourney.error.countryCode") : ""
              }
              icon={
                <div className="flex size-lg items-center justify-center">
                  <img
                    alt="countryFlag"
                    height={20}
                    src={countryFlagUrl}
                    width={20}
                  />
                </div>
              }
              iconPosition="left"
            />
          </div>

          <div className="w-full">
            <TextField
              defaultValue={phoneNumberShaper(formData.phone ?? "")}
              id="phone"
              label={t("addressJourney.addressForm.phoneNumber")}
              {...register("phone", {
                required: true,
              })}
              error={
                Boolean(errors.phone) ||
                addressFormStates.errorStates.invalidPhoneNumber
              }
              haveBoldText
              helperText={getPhoneNumberError(
                errors,
                addressFormStates.errorStates.invalidPhoneNumber,
                t,
              )}
              icon={<PhoneIcon />}
              iconPosition="left"
              onChange={(e) => {
                handlePhoneChange(e);
              }}
              placeholder={t("addressJourney.addressForm.placeHolders.phone")}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
